import WRAP from "WRAP-JS";
import { WX_ID_SORACAMELIGHT } from "../../../store/WeatherContentsState";
import { configSoraCameDh, configSoraCameLayer } from "./config/ConfigSoraCame";
import {
  COMMUNICATE_ERROR_MESSAGE_SORACAME_LIST,
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD,
} from "../../../constants/ErrorMessage";
import { convertWRAPPoint } from "../../../utils/FPEUtils";

class SoraCame {
  static NAME = WX_ID_SORACAMELIGHT;

  layer;
  dispatcher;
  ready;
  visible;
  fitMode = 'screen';

  constructor() {
    this.layer = new WRAP.Geo.Layer(SoraCame.NAME);
    this.layer.setVisible(false);
    this._queue = new Set();
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  initialize() {
    this.initData();
    this.initLayer();
    this.ready = true;
  }

  convertToGeoJSON(list) {
    const features = [];
    list.forEach((l) => {
      features.push({
        type: "Features",
        geometry: {
          type: "Point",
          coordinates: [l.lon, l.lat],
        },
        properties: l,
      });
    });
    return {
      type: "FeatureCollection",
      features: features,
    };
  }

  initData() {
    this.dh = new WRAP.Data(configSoraCameDh);
    this.dh.updated(
      "data",
      () => {},
      () => {
        this.dispatcher({
          type: "SHOW_ERROR",
          payload: {
            message: COMMUNICATE_ERROR_MESSAGE_SORACAME_LIST,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD,
          },
        });
      }
    );
  }

  initLayer() {
    this.layer.configure(this.dh, configSoraCameLayer);
    this.layer._renderer.inspect(this._layerInspector);
  }

  drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this.layer;
  }

  setVisible(visible) {
    if (!this.ready) return;

    if (visible) {
      this.layer.show();
    } else {
      this.layer.hide();
    }
  }

  getLegend() {
    return {
      id: SoraCame.NAME,
      label: "ソラカメ",
    };
  }

  changeTimeZone(tz) {
    let attr;
    if (
      configSoraCameLayer.Attributes.timezone &&
      configSoraCameLayer.Attributes.timezone[tz]
    ) {
      attr = configSoraCameLayer.Attributes.timezone[tz];
    } else {
      attr = {};
    }
    this.layer.setAttributes(attr);
  }

  getExtendedOperation() {
    if (!this.layer._renderer || !this.layer._renderer.camera_window) {
      return;
    }
    const cameras = this.layer._renderer.camera_window.filter(w => w.visible && !w.tooltip).reduce((previous, w) => {
      previous[w.camera.id] = {
        id: w.camera.id,
        dispLon: w.window.point.lonDegree(),
        dispLat: w.window.point.latDegree(),
        dispTop: parseFloat(w.window.style.top),
        dispLeft: parseFloat(w.window.style.left),
        small: w.small,
      }
      return previous;
    }, {});
    return {
      camera: {
        function: "setInitCameras",
        value: cameras
      }
    };
  }


  async setInitCameras(cameras) {
    this.initCameras = Object.assign({}, cameras);
    // Contents Set対応。描画済みのカメラを消して、this.initCamerasの情報で再描画
    if (this.layer._renderer !== undefined) {
      const overlay = this.layer._renderer.getOverlay();
      if (overlay) {
        overlay.clearWindow();
        if (Object.keys(this.initCameras).length > 0) {
          const drawInitCamera = () => {
            Object.keys(this.layer._renderer.camera_info).forEach(c => {
              this._drawInitCamera({
                camera: c,
                type: "CameraInfoResponse"
              });
            });
          }

          if (this.layer.visible()) {
            drawInitCamera();
          } else {
            // layer非表示の状態ではVisibilityChangeイベントが発火した後でないと、描画できないため待ち受ける
            this._queue.add(drawInitCamera);
          }
        }
      }
    }
  }


  _layerInspector = _ins => {
    if (_ins.type === 'CameraInfoResponse') {
      this._drawInitCamera(_ins);
    } else if (_ins.type === 'LayerShow') {
      // initCamera描画可能状態になったので（queueにつまっていたdrawInitCameraを実行）
      for (const fn of this._queue) {
        fn();
      }
      this._queue.clear();
    }
  }

  _drawInitCamera = _ins => {
    if (this.initCameras) {
      if (_ins.camera in this.initCameras) {
        const w = this.initCameras[_ins.camera];
        this.layer._renderer.showCameraWindow(this.layer._renderer.camera_info[w.id], this.layer);
        const current_camera = this.layer._renderer.currentCamera();
        current_camera.window.controller.pinned = true;
        if (this.fitMode === 'screen') {
          const {width: screenWidth, height: screenHeight} = WRAP.Geo.getSize();
          let left = w.dispLeft;
          let top = w.dispTop;
          if (screenWidth - w.dispLeft < this.constructor.MIN_SIZE_X) {
            left = screenWidth - this.constructor.MIN_SIZE_X;
          }
          if (screenHeight - w.dispTop < this.constructor.MIN_SIZE_Y) {
            top = screenHeight - this.constructor.MIN_SIZE_Y;
          }
          const sp = new WRAP.Geo.ScreenPoint(left, top);
          const point = WRAP.Geo.getPoint(sp);
          current_camera.window.point = point;
          current_camera.window.style.left = left + 'px';
          current_camera.window.style.top = top + 'px';
        } else if (this.fitMode === 'latlon') {
          const point = convertWRAPPoint(w.dispLat, w.dispLon);
          const sp = WRAP.Geo.getScreenPoint(point);
          current_camera.window.point = point;
          current_camera.window.style.left = sp.x + 'px';
          current_camera.window.style.top = sp.y + 'px';
        }
        if (current_camera.tooltip) {
          current_camera.tooltip = false;
        }
        current_camera.smooth = true;
        this.layer._renderer.update(w.small ? 'WindowMinimize' : 'WindowMaximize', w.id);
        current_camera.small = w.small;
        current_camera.set();
        current_camera.updateImageStatus();
        delete this.initCameras[_ins.camera];
      }
    }
  }

}

export default SoraCame;
